<template>
	<div class="delete-form">
		<CCallout :type="calloutType" :title="hasConnectedForms ? calloutMessage : null">
			<div v-if="!hasConnectedForms">
				<p v-if="useFormName">
					Are you sure you want to{{ entries > 0 ? ' permanently' : '' }} delete this form<span v-if="entries > 0"> and the <strong>{{ entries === 1 ? '1 entry' : `${entries} entries` }}</strong> it has collected</span>?
				</p>
				<p v-else>
					{{ calloutMessage }}
				</p>
			</div>
			<div v-for="(connections, id) in connectedForms" :key="id">
				<i>{{ formNames[id] }} is referenced by {{ connections.length }} other form{{ connections.length > 1 ? 's' : '' }}</i>
			</div>
		</CCallout>
		<div>
			<p class="mt-4 mb-3">{{ useFormName ? 'Input the name of the form you want to delete.': 'Please type DELETE below to confirm.' }}</p>
			<CInput id="delete-forms-prompt" v-model="input" autocomplete="off" :placeholder="useFormName ? formName : 'DELETE'" />
		</div>
		<div class="dialog__footer">
			<CStatusButton class="button button button--secondary mt-4" status="default" @click.native="$emit('cancel')">
				Cancel
			</CStatusButton>
			<CStatusButton class="button button--primary mt-4" :status="buttonStatus" :auto-reset="false" @click.native="handleDelete">
				Delete
			</CStatusButton>
		</div>
	</div>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import { computed, defineComponent, ref } from 'vue';
	import type { CalloutType } from 'src/components/content/Callout.vue';
	import CCallout from 'src/components/content/Callout.vue';
	import CInput from 'src/components/form-controls/Input.vue';
	import type { ButtonStatus } from 'src/components/interaction/StatusButton.vue';
	import CStatusButton from 'src/components/interaction/StatusButton.vue';
	import type { LookupFormReference } from '@cognitoforms/types/server-types/services/forms/lookup-form-reference';

	export default defineComponent({
		name: 'DeleteForms',
		components: {
			CCallout,
			CInput,
			CStatusButton
		},
		props: {
			// List of form names keyed by form ID
			formNames: {
				type: Object as PropType<Record<string, string>>,
				required: true
			},
			entries: {
				type: Number,
				required: true
			},
			// List of connected forms keyed by form ID
			connectedForms: {
				type: Object as PropType<Record<string, LookupFormReference[]>>,
				required: true
			},
			allowUseFormName: {
				type: Boolean,
				default: false
			}
		},
		emits: ['confirm-deletion', 'cancel'],
		setup(props, { emit }) {
			const formCount = Object.keys(props.formNames).length;
			const input = ref('');

			const useFormName = props.allowUseFormName && formCount === 1;
			const formName = props.formNames[Object.keys(props.formNames)[0]];

			const calloutMessage =
				'Are you sure you want to' + (props.entries > 0 ? ' permanently' : '') + ' delete '
				+ (formCount > 1 ? `${formCount} forms` : 'this form')
				+ (props.entries > 1 ? ` and ${props.entries} entries` : (props.entries === 1 ? ' and 1 entry' : '')) + '?';

			const referenceCount = new Set(Object.values(props.connectedForms).flatMap(f => f)).size;
			const calloutType: CalloutType = (referenceCount !== 0) ? 'error' : 'warning';

			const allowDelete = computed(() => useFormName ? input.value === formName : input.value === 'DELETE');
			const buttonStatus = computed<ButtonStatus>(() => allowDelete.value ? 'default' : 'disabled');

			const handleDelete = () => {
				if (allowDelete.value)
					emit('confirm-deletion');
			};

			return {
				calloutMessage,
				useFormName,
				formName,
				calloutType,
				buttonStatus,
				input,
				handleDelete,
				hasConnectedForms: computed(() => Object.keys(props.connectedForms).length > 0)
			};
		}
	});
</script>